/* global $ */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isString, trim, isEmpty } from 'lodash';

import EplusPropTypes from '../../../propTypes';
import FormControlErrorMessage from '../../utils/FormControlErrorMessage';
import FormControlInfoMessage from '../../utils/FormControlInfoMessage';
import Icon from '../../utils/Icon';
import TranslationContext from '../../utils/Translations/TranslationContext';
import ModalWidget from '../../widgets/ModalWidget';
import AnimatedTitle from './AnimatedTitle';
import TextArea from './TextArea';

import style from './TextField.scss';
import { withInputDirection } from './withInputDirection';

const BLUR_TIMEOUT = 0;

@withInputDirection
export default class TextField extends React.PureComponent {
  static propTypes = {
    type: EplusPropTypes.type,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.number.isRequired,
    ]),
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onPaste: PropTypes.func,
    isChangeRaisedOnBlur: PropTypes.bool,
    onEnter: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    inputProps: PropTypes.object,
    disabled: PropTypes.bool,
    leftIcon: EplusPropTypes.icon,
    placeholder: PropTypes.string,
    errorMessage: EplusPropTypes.formErrorMessage,
    infoMessage: PropTypes.string,
    infoMessageAlign: PropTypes.oneOf(['left', 'right', 'right_end']),
    infoMessageClassName: PropTypes.string,
    animateTitle: PropTypes.bool,
    formElement: PropTypes.bool,
    inputContainer: PropTypes.bool,
    noLabel: PropTypes.bool,
    description: PropTypes.bool,
    required: PropTypes.bool,
    addonAlign: PropTypes.oneOf(['left', 'right']),
    renderAddon: PropTypes.func,
    renderHelpBlock: PropTypes.func,
    draggable: PropTypes.node,
    change: PropTypes.func,
    member: PropTypes.string,
    data: PropTypes.object,
    flex: PropTypes.bool,
    max: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    onBlurField: PropTypes.func,
    maskOptions: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        regex: PropTypes.string,
        mask: PropTypes.string,
        definitions: PropTypes.object,
      }),
    ]),
    refFunc: PropTypes.func,
    autocomplete: PropTypes.string,
    role: PropTypes.string,
    hasFocusOnFirstRender: PropTypes.bool,
    hasStrictLengthLimit: PropTypes.bool,
    hasInputTitle: PropTypes.bool,
    inputClassName: PropTypes.string,
    hasCaretPositionPreserve: PropTypes.bool,
    emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    errorClassName: PropTypes.string,
    errorIsStatic: PropTypes.bool,
    errorNoMargin: PropTypes.bool,
    translateErrorMessage: PropTypes.bool,
    labelIcon: PropTypes.element,
    labelIconPosition: PropTypes.oneOf(['left', 'right']),
    labelClassName: PropTypes.string,
    trimOnBlur: PropTypes.bool,
    dataCy: PropTypes.string,
    tooltipTitle: PropTypes.string,
  };

  static defaultProps = {
    type: 'text',
    name: null,
    value: '',
    leftIcon: null,
    errorMessage: null,
    infoMessage: null,
    infoMessageAlign: 'left',
    infoMessageClassName: '',
    placeholder: '',
    inputProps: {},
    disabled: false,
    animateTitle: false,
    formElement: true,
    required: false,
    renderAddon: null,
    addonAlign: 'right',
    onBlur: null,
    isChangeRaisedOnBlur: true,
    onFocus: null,
    onEnter: null,
    onPaste: null,
    renderHelpBlock: null,
    className: null,
    noLabel: false,
    draggable: null,
    inputContainer: null,
    description: false,
    change: null,
    member: null,
    data: {},
    flex: false,
    hasFocusOnFirstRender: false,
    maskOptions: undefined,
    max: undefined,
    refFunc: null,
    onBlurField: null,
    autocomplete: 'off',
    role: 'presentation',
    hasStrictLengthLimit: false,
    hasInputTitle: true,
    inputClassName: '',
    hasCaretPositionPreserve: true,
    emptyValue: null,
    errorClassName: '',
    errorIsStatic: false,
    errorNoMargin: false,
    labelIcon: <></>,
    labelIconPosition: 'right',
    trimOnBlur: true,
    dataCy: '',
    tooltipTitle: '',
  };

  state = {
    visible: false,
    errorMessageState: null,
  };

  async componentDidMount() {
    const { hasFocusOnFirstRender } = this.props;
    let { maskOptions } = this.props;

    if (hasFocusOnFirstRender) {
      this.inputRef.focus();
    }

    if (!maskOptions) {
      return;
    }

    const { default: Inputmask } =
      typeof window !== 'undefined' ? await import('inputmask') : {};

    if (isString(maskOptions)) {
      maskOptions = { mask: maskOptions };
    }

    Inputmask({
      ...maskOptions,
      showMaskOnHover: false,
      showMaskOnFocus: false,
    }).mask(this.inputRef);
  }

  componentWillUnmount() {
    this.timerId && window.clearTimeout(this.timerId);
  }

  static contextType = TranslationContext;

  blurTimerId = 0;

  handleModalToggle = () =>
    this.setState(state => ({ visible: !state.visible }));

  renderAnimatedTitle() {
    const {
      value,
      placeholder,
      required,
      label,
      labelIcon,
      labelIconPosition,
      labelClassName,
      tooltipTitle,
    } = this.props;

    return (
      <AnimatedTitle
        className={labelClassName}
        icon={labelIcon}
        iconPosition={labelIconPosition}
        isRequired={required}
        placeholder={label || placeholder}
        tooltipTitle={tooltipTitle}
        value={value}
      />
    );
  }

  renderErrorMessage() {
    const {
      errorMessage,
      errorClassName,
      errorIsStatic,
      errorNoMargin,
      translateErrorMessage,
    } = this.props;
    const { errorMessageState } = this.state;
    return (
      <FormControlErrorMessage
        className={errorClassName}
        errorMessage={errorMessage || errorMessageState}
        isStatic={errorIsStatic}
        noMargin={errorNoMargin}
        translateErrorMessage={
          translateErrorMessage ?? (Boolean(errorMessage) || !errorMessageState)
        }
      />
    );
  }

  renderInfoMessage() {
    const { infoMessage, infoMessageAlign, infoMessageClassName } = this.props;

    return (
      <FormControlInfoMessage
        additionalClassNames={infoMessageClassName}
        message={infoMessage}
        messageAlign={infoMessageAlign}
      />
    );
  }

  renderLeftIcon() {
    const { leftIcon } = this.props;

    return (
      <div className="form-control-feedback">
        <Icon className="text-muted" name={leftIcon} />
      </div>
    );
  }

  handleSubmit = values => {
    const { change, member } = this.props;

    values.forEach((value, key) => change(`${member}.${key}`, value));

    this.handleModalToggle();
  };

  renderDescription = () => {
    const { visible } = this.state;
    const { data } = this.props;
    return (
      <ModalWidget
        top
        entity={data}
        entityName="description"
        icon="file-text"
        title="Section Title"
        visible={visible}
        onCancel={this.handleModalToggle}
        onModalToggle={this.handleModalToggle}
        onSubmit={this.handleSubmit}
      >
        <TextArea label="Description" name="description" rows={2} />
      </ModalWidget>
    );
  };

  handleBlur = event => {
    const {
      onBlur,
      onChange,
      isChangeRaisedOnBlur,
      emptyValue,
      name,
      onBlurField,
      trimOnBlur,
    } = this.props;

    event.persist();

    const value = trimOnBlur ? trim(event.target.value) : event.target.value;
    this.inputRef.value = isEmpty(value) ? emptyValue : value;

    this.timerId = window.setTimeout(() => {
      if (onBlurField) onBlurField(event);
      isChangeRaisedOnBlur && onChange({ ...event, target: { name, value } });
      onBlur && onBlur(event);
    }, BLUR_TIMEOUT);
  };

  handleFocus = event => {
    const { onFocus } = this.props;
    onFocus && onFocus(event);
  };

  handleChange = event => {
    const { minLength, maxLength } = this.props;
    const _value = event.target.value;
    if (maxLength && _value.length > maxLength) {
      this.setState({
        errorMessageState: this.context.t('Max length is #{maxLength}', {
          maxLength,
        }),
      });
      event.target.value = _value.substring(0, maxLength);
    } else if (minLength && _value.length < minLength) {
      this.setState({
        errorMessageState: this.context.t('Min length is #{minLength}', {
          minLength,
        }),
      });
      event.target.value = _value.substring(0, minLength);
    } else {
      event.target.value = _value;
      this.setState({ errorMessageState: null });
    }
    const { onChange, hasCaretPositionPreserve } = this.props;
    event.persist();

    const caretPosition = this.inputRef?.selectionStart;
    onChange(event);

    if (hasCaretPositionPreserve && caretPosition) {
      this.inputRef.selectionStart = caretPosition;
      this.inputRef.selectionEnd = caretPosition;
    }
  };
  handlePaste = event => {
    const { onPaste, name } = this.props;
    onPaste && onPaste(event, name);
  };
  handleKeyPress = event => {
    const ENTER = 13;
    const code = event.keyCode || event.which;
    if (code === ENTER) {
      const { onEnter } = this.props;
      if (onEnter) {
        event.preventDefault();
        event.stopPropagation();
        onEnter(event);
      }
    }
  };

  bindInputToContext = inputRef => {
    if (!inputRef) {
      return;
    }

    this.inputRef = inputRef;

    const { refFunc } = this.props;
    refFunc && refFunc(inputRef);
  };

  get inputFieldCore() {
    const {
      name,
      placeholder,
      type,
      value,
      inputProps: { className, onBlur, ...restInputProps },
      max,
      disabled,
      required,
      autocomplete,
      role,
      minLength,
      maxLength,
      hasStrictLengthLimit,
      hasInputTitle,
      inputClassName,
      dataCy,
      tooltipTitle,
    } = this.props;
    const inputTitle =
      type === 'password'
        ? ''
        : tooltipTitle || (hasInputTitle && value) || placeholder || '';

    return (
      <input
        ref={this.bindInputToContext}
        autoComplete={autocomplete}
        className={classnames(
          'form-control',
          className,
          style.inputWidth,
          inputClassName,
        )}
        data-cy={dataCy}
        disabled={disabled}
        max={max}
        maxLength={hasStrictLengthLimit ? maxLength : undefined}
        minLength={hasStrictLengthLimit ? minLength : undefined}
        name={name}
        placeholder={required ? `${placeholder} *` : placeholder}
        role={role}
        title={inputTitle}
        type={type}
        value={value}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onFocus={this.handleFocus}
        onKeyPress={this.handleKeyPress}
        onPaste={this.handlePaste}
        {...restInputProps}
      />
    );
  }

  get inputField() {
    const { addonAlign, renderAddon, renderHelpBlock } = this.props;

    let inputField = this.inputFieldCore;

    if (renderHelpBlock) {
      inputField = (
        <>
          {inputField}
          <span
            className={classnames(
              'help-block',
              this.context.isLayoutRTL ? 'pull-left' : 'pull-right',
            )}
          >
            {renderHelpBlock()}
          </span>
        </>
      );
    }

    if (renderAddon) {
      inputField = (
        <div className="input-group">
          {addonAlign === 'left' ? (
            <>
              <span className="input-group-addon no-padding">
                {renderAddon()}
              </span>
              {inputField}
            </>
          ) : (
            <>
              {inputField}
              <span className="input-group-addon">{renderAddon()}</span>
            </>
          )}
        </div>
      );
    }

    return inputField;
  }

  render() {
    const {
      errorMessage,
      infoMessage,
      leftIcon,
      animateTitle,
      formElement,
      className,
      inputContainer,
      noLabel,
      draggable,
      description,
      flex,
    } = this.props;

    const { errorMessageState } = this.state;

    return (
      <div
        className={classnames(className, {
          'form-group': formElement,
          'has-feedback': !!leftIcon,
          'has-feedback-left': !!leftIcon,
          'form-group-material': animateTitle,
          'input-container': inputContainer,
          'input-group': !!draggable,
          [style.flex]: flex,
        })}
        data-layout-direction={this.context.direction}
        dir={this.context.direction}
      >
        {animateTitle && !noLabel && this.renderAnimatedTitle()}
        {draggable}
        {this.inputField}
        {description && this.renderDescription()}
        {(errorMessageState || errorMessage) && this.renderErrorMessage()}
        {infoMessage &&
          !errorMessageState &&
          !errorMessage &&
          this.renderInfoMessage()}
        {leftIcon && this.renderLeftIcon()}
      </div>
    );
  }
}
